// c021 지역3(서리)·지역4(독 포자), C2 지역5(모래)·지역6(심연) 몹·보스 변형: 기존 골격 모델 위에 붙이는 조각(public/assets/regions/<기본>__<look>.glb).
// 조각은 Blender 헤드리스(tools/blender/r34_variants.py)가 기본 모델의 '쉬는 자세'(aActorRest와 같은 공간) 표면에 만든 정적 메시이고,
// 노드 이름 'att_<본>'(원본 메시) / 'lod_<본>'(저폴리 판)이 붙을 본을 가리킨다. 정점색 COLOR_0 = (선형 알베도 RGB, A = 발광 0..1).
// 로드 때 한 번: 쉬는 자세 좌표를 그 본의 바인드 공간으로 되돌려(S_b^-1) 원본 지오메트리 뒤에 이어 붙이고 가중치 1로 묶는다.
// 그래서 기존 골격 텍스처·클립(달리기·공격·피격·사망)을 그대로 타고, 인스턴싱·외곽선·디더도 같은 메시 하나로 처리된다.
// 프레임 루프에서는 아무것도 하지 않는다.
import * as THREE from 'three/webgpu';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/addons/libs/meshopt_decoder.module.js';
import { resolveAssetUrl } from '../../pwa/assetMap';

export type RegionLook = 'frost' | 'poison' | 'sand' | 'void';
export const REGION_LOOKS: readonly RegionLook[] = ['frost', 'poison', 'sand', 'void'];
/** 지역 → 변형(지역3 서리 묘지, 지역4 가라앉은 사원, C2 지역5 황금 모래 폐도, 지역6 심연의 성채). 나머지 지역은 변형 없음 */
export const REGION_LOOK: Readonly<Record<string, RegionLook | undefined>> = { r3: 'frost', r4: 'poison', r5: 'sand', r6: 'void' };
const LOOK_BY_REGION: readonly (RegionLook | null)[] = [null, null, null, 'frost', 'poison', 'sand', 'void'];
/** 보스 정의의 지역 번호 → 변형 */
export const lookOfRegion = (region: number): RegionLook | null => LOOK_BY_REGION[region] ?? null;

type RGB = readonly [number, number, number];
/**
 * 보스 조각 재질 값(근접형 고목 원형·시전형 잿불 원형 공통): 조각 발광 배율, 맥동 폭, 가장자리 반짝임(조각만),
 * 고목 가슴 결정 발광 색, 잿불 지팡이 구슬 색. null = 변형 없는 지역(R1·R2 원래 값)
 */
export interface BossLookMaterial { readonly gain: number; readonly pulse: number; readonly sparkle: RGB; readonly crystal: RGB; readonly orb: RGB }
export const BOSS_LOOK_BASE: BossLookMaterial = { gain: 1.5, pulse: 0, sparkle: [0, 0, 0], crystal: [0.45, 1.0, 0.4], orb: [1.6, 0.12, 0.005] };
export const BOSS_LOOK_MATERIAL: Readonly<Record<RegionLook, BossLookMaterial>> = {
  frost: { gain: 1.5, pulse: 0, sparkle: [0.35, 0.75, 1.0], crystal: [0.4, 0.85, 1.35], orb: [0.35, 1.05, 1.9] },
  poison: { gain: 1.7, pulse: 0.45, sparkle: [0.3, 0.65, 0.08], crystal: [0.45, 1.0, 0.4], orb: [0.7, 1.7, 0.1] },
  // 모래: 금빛 반짝임 + 청록 보석(결정·구슬), 맥동 없음
  sand: { gain: 1.55, pulse: 0, sparkle: [0.62, 0.48, 0.12], crystal: [0.22, 1.2, 1.05], orb: [0.2, 1.45, 1.3] },
  // 심연: 자홍 반짝임, 약한 맥동, 보라 결정·구슬
  void: { gain: 1.7, pulse: 0.25, sparkle: [0.7, 0.14, 0.62], crystal: [0.85, 0.3, 1.45], orb: [1.1, 0.28, 1.9] },
};
export const bossLookMaterial = (look: RegionLook | null): BossLookMaterial => (look ? BOSS_LOOK_MATERIAL[look] : BOSS_LOOK_BASE);

/** 조각 한 덩이(한 본에 묶인다). 좌표는 기본 모델 쉬는 자세(월드, 발이 원점) */
export interface AttachPart {
  readonly bone: string;
  readonly position: Float32Array;
  readonly normal: Float32Array;
  /** RGBA: 선형 알베도 + 발광 */
  readonly color: Float32Array;
  readonly index: Uint32Array;
}
export interface AttachSet { readonly hi: readonly AttachPart[]; readonly lod: readonly AttachPart[] }
/** 조각이 없는 묶음: 보스 원형에 빈 aAtt 속성만 붙여 변형 메시와 같은 재질을 쓰게 한다 */
export const EMPTY_ATTACH: AttachSet = { hi: [], lod: [] };

const base = (): string => import.meta.env.BASE_URL ?? './';
export const regionAttachUrl = (name: string, look: RegionLook): string => resolveAssetUrl(base() + 'assets/regions/' + name + '__' + look + '.glb');

/** 조각 GLB를 읽어 본별 덩이로 편다. 실패하면 null(호출한 쪽은 색조판으로 대신한다) */
export async function loadAttachments(name: string, look: RegionLook): Promise<AttachSet | null> {
  try {
    const gltf = await new GLTFLoader().setMeshoptDecoder(MeshoptDecoder).loadAsync(regionAttachUrl(name, look));
    return attachSetFromScene(gltf.scene);
  } catch (err) {
    console.warn('지역 변형 조각을 읽지 못해 색조판으로 대신한다: ' + name + '__' + look, err);
    return null;
  }
}

/** 장면의 'att_<본>'·'lod_<본>' 메시를 덩이로 편다(양자화 해제 + 노드 변환 적용) */
export function attachSetFromScene(scene: THREE.Object3D): AttachSet {
  const hi: AttachPart[] = [], lod: AttachPart[] = [];
  scene.updateMatrixWorld(true);
  const nm = new THREE.Matrix3(), v = new THREE.Vector3();
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (!mesh.isMesh) return;
    const tag = mesh.name.startsWith('att_') ? hi : mesh.name.startsWith('lod_') ? lod : null;
    if (!tag) return;
    const g = mesh.geometry;
    const pos = g.getAttribute('position'), nrm = g.getAttribute('normal'), col = g.getAttribute('color');
    const n = pos.count;
    const position = new Float32Array(n * 3), normal = new Float32Array(n * 3), color = new Float32Array(n * 4);
    nm.getNormalMatrix(mesh.matrixWorld);
    for (let i = 0; i < n; i++) {
      v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld).toArray(position, i * 3);
      if (nrm) v.fromBufferAttribute(nrm, i).applyMatrix3(nm).normalize().toArray(normal, i * 3);
      else normal[i * 3 + 1] = 1;
      if (col) {
        color[i * 4] = col.getX(i); color[i * 4 + 1] = col.getY(i); color[i * 4 + 2] = col.getZ(i);
        color[i * 4 + 3] = col.itemSize > 3 ? col.getW(i) : 0;
      } else { color[i * 4] = color[i * 4 + 1] = color[i * 4 + 2] = 0.8; }
    }
    const idx = g.getIndex();
    const index = idx ? Uint32Array.from(idx.array as ArrayLike<number>) : Uint32Array.from({ length: n }, (_, i) => i);
    // GLTFLoader가 노드 이름을 정리한다(':'·'.' 제거) — 몸 골격 본 이름도 같은 규칙으로 정리돼 있다
    tag.push({ bone: mesh.name.slice(4), position, normal, color, index });
  });
  return { hi, lod };
}

/**
 * 원본 지오메트리(월드로 편 바인드 공간 + skinIndex/skinWeight + aSmooth·aActorRest(+aPaint))에 조각을 이어 붙인 새 지오메트리.
 * 모든 정점에 aAtt(vec4)를 단다: 원본 (0,0,0,0), 조각 (알베도 RGB, 1 + 발광). 셰이더는 w ≥ 0.5를 조각으로 본다.
 * 쉬는 자세 → 바인드: S_b = world · bindInv · (본 월드 · 본 역바인드) · bind · worldInv (aActorRest를 만든 변환과 같다)의 역행렬.
 * 붙일 본을 못 찾은 덩이는 버리고 경고한다(원본은 그대로).
 */
export function appendAttachments(geometry: THREE.BufferGeometry, mesh: THREE.SkinnedMesh, parts: readonly AttachPart[], label = ''): THREE.BufferGeometry {
  const bones = mesh.skeleton.bones;
  mesh.updateMatrixWorld(true);
  mesh.skeleton.update();
  const world = mesh.matrixWorld, worldInv = world.clone().invert();
  const used: { part: AttachPart; bone: number; inv: THREE.Matrix4 }[] = [];
  const bm = new THREE.Matrix4();
  for (const part of parts) {
    const bi = bones.findIndex(b => b.name === part.bone);
    if (bi < 0) { console.warn('지역 변형 조각의 본을 찾지 못해 뺀다: ' + label + ' ' + part.bone); continue; }
    bm.fromArray(mesh.skeleton.boneMatrices!, bi * 16);
    const s = world.clone().multiply(mesh.bindMatrixInverse).multiply(bm).multiply(mesh.bindMatrix).multiply(worldInv);
    used.push({ part, bone: bi, inv: s.invert() });
  }
  const n0 = geometry.getAttribute('position').count;
  const nAdd = used.reduce((s, u) => s + u.part.position.length / 3, 0);
  const n = n0 + nAdd;
  const names = Object.keys(geometry.attributes);
  const out = new THREE.BufferGeometry();
  const arrays: Record<string, Float32Array> = {};
  for (const key of names) {
    const src = geometry.getAttribute(key);
    const arr = new Float32Array(n * src.itemSize);
    for (let i = 0; i < n0; i++) for (let c = 0; c < src.itemSize; c++) arr[i * src.itemSize + c] = src.getComponent(i, c);
    arrays[key] = arr;
  }
  const att = new Float32Array(n * 4);
  const idx0 = geometry.getIndex();
  const i0 = idx0 ? idx0.count : n0;
  const iAdd = used.reduce((s, u) => s + u.part.index.length, 0);
  const index = new Uint32Array(i0 + iAdd);
  for (let i = 0; i < i0; i++) index[i] = idx0 ? idx0.getX(i) : i;
  // 조각 매끈 법선(외곽선 부풀림): 조각만 따로 계산한다(원본 몸과 위치가 겹치지 않는다)
  const tmp = new THREE.Vector3(), nrm = new THREE.Vector3(), nmat = new THREE.Matrix3();
  let vo = n0, io = i0;
  for (const { part, bone, inv } of used) {
    const m = part.position.length / 3;
    nmat.getNormalMatrix(inv);
    const smooth = smoothNormals(part);
    for (let k = 0; k < m; k++) {
      const vi = vo + k;
      tmp.fromArray(part.position, k * 3);
      const rest = arrays.aActorRest;
      if (rest) { rest[vi * 3] = tmp.x; rest[vi * 3 + 1] = tmp.y; rest[vi * 3 + 2] = tmp.z; }
      tmp.applyMatrix4(inv).toArray(arrays.position, vi * 3);
      if (arrays.normal) nrm.fromArray(part.normal, k * 3).applyMatrix3(nmat).normalize().toArray(arrays.normal, vi * 3);
      if (arrays.aSmooth) nrm.fromArray(smooth, k * 3).applyMatrix3(nmat).normalize().toArray(arrays.aSmooth, vi * 3);
      if (arrays.skinIndex) { arrays.skinIndex[vi * 4] = bone; }
      if (arrays.skinWeight) { arrays.skinWeight[vi * 4] = 1; }
      if (arrays.aPaint) { arrays.aPaint[vi * 3] = part.color[k * 4]; arrays.aPaint[vi * 3 + 1] = part.color[k * 4 + 1]; arrays.aPaint[vi * 3 + 2] = part.color[k * 4 + 2]; }
      att[vi * 4] = part.color[k * 4]; att[vi * 4 + 1] = part.color[k * 4 + 1]; att[vi * 4 + 2] = part.color[k * 4 + 2];
      att[vi * 4 + 3] = 1 + part.color[k * 4 + 3];
    }
    for (let k = 0; k < part.index.length; k++) index[io + k] = part.index[k] + vo;
    vo += m; io += part.index.length;
  }
  for (const key of names) out.setAttribute(key, new THREE.BufferAttribute(arrays[key], geometry.getAttribute(key).itemSize));
  out.setAttribute('aAtt', new THREE.BufferAttribute(att, 4));
  out.setIndex(new THREE.BufferAttribute(index, 1));
  out.boundingBox = geometry.boundingBox?.clone() ?? null;
  return out;
}

/** 조각 정점 법선을 같은 위치끼리 평균 낸 매끈 법선(결정 끝·갓 가장자리에서 외곽선이 갈라지지 않게) */
function smoothNormals(part: AttachPart): Float32Array {
  const n = part.position.length / 3;
  const key = new Map<string, number>();
  const group = new Int32Array(n);
  for (let i = 0; i < n; i++) {
    const k = Math.round(part.position[i * 3] * 1e4) + ',' + Math.round(part.position[i * 3 + 1] * 1e4) + ',' + Math.round(part.position[i * 3 + 2] * 1e4);
    let g = key.get(k);
    if (g === undefined) { g = key.size; key.set(k, g); }
    group[i] = g;
  }
  const acc = new Float32Array(key.size * 3);
  for (let i = 0; i < n; i++) { const g = group[i] * 3; acc[g] += part.normal[i * 3]; acc[g + 1] += part.normal[i * 3 + 1]; acc[g + 2] += part.normal[i * 3 + 2]; }
  const out = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) {
    const g = group[i] * 3, l = Math.hypot(acc[g], acc[g + 1], acc[g + 2]) || 1;
    out[i * 3] = acc[g] / l; out[i * 3 + 1] = acc[g + 1] / l; out[i * 3 + 2] = acc[g + 2] / l;
  }
  return out;
}
